import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, inject, input, output } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { startWith, switchMap } from 'rxjs';
import { SurveyQuestionTypeEnum, SurveyQuestionTypeEnumLabels, hasEditableResponseOptions } from 'src/app/enums/survey-question-type.enum';
import {
  SurveyVariableConditionSectionQuestionAnonymousScopeEnum,
  SurveyVariableConditionSectionQuestionAnonymousScopeEnumLabels,
} from 'src/app/enums/survey-variable-condition-section-question-anonymous-scope.enum';
import {
  MIN_CHARACTERS_MAX,
  MIN_CHARACTERS_MIN,
  SurveySectionQuestionForm,
  SurveySectionQuestionItemForm,
} from 'src/app/models/survey-variable-condition-section.model';

@Component({
  selector: 'app-survey-section-question-edit',
  templateUrl: './survey-section-question-edit.component.html',
  styleUrl: './survey-section-question-edit.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    DragDropModule,
    MatButtonModule,
    MatCheckboxModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
  ],
})
export class SurveySectionQuestionEditComponent {
  private readonly form_builder = inject(FormBuilder);
  private readonly change_detector = inject(ChangeDetectorRef);

  readonly question_form = input.required<SurveySectionQuestionForm>();
  readonly question_index = input.required<number>();
  readonly disabled = input(false);
  readonly remove = output<void>();

  private readonly statement_value = toSignal(
    toObservable(this.question_form).pipe(
      switchMap(form => form.controls.statement.valueChanges.pipe(startWith(form.controls.statement.value)))
    ),
    { initialValue: '' }
  );

  protected readonly header_title = computed(() => {
    const statement = this.statement_value().trim();

    return statement !== '' ? statement : `Pregunta ${this.question_index() + 1}`;
  });

  private readonly selected_question_type = toSignal(
    toObservable(this.question_form).pipe(switchMap(form => form.controls.type.valueChanges.pipe(startWith(form.controls.type.value)))),
    { initialValue: SurveyQuestionTypeEnum.Text }
  );

  protected readonly is_text_question = computed(() => this.selected_question_type() === SurveyQuestionTypeEnum.Text);
  protected readonly show_editable_response_options = computed(() => hasEditableResponseOptions(this.selected_question_type()));

  protected readonly text_min_characters_min = MIN_CHARACTERS_MIN;
  protected readonly text_min_characters_max = MIN_CHARACTERS_MAX;

  protected readonly question_type_options = Object.values(SurveyQuestionTypeEnum).map(type => ({
    value: type,
    label: SurveyQuestionTypeEnumLabels[type],
  }));

  protected readonly anonymous_scope_options = Object.values(SurveyVariableConditionSectionQuestionAnonymousScopeEnum).map(scope => ({
    value: scope,
    label: SurveyVariableConditionSectionQuestionAnonymousScopeEnumLabels[scope],
  }));

  setExpanded(expanded: boolean): void {
    this.question_form().controls.expanded.setValue(expanded);
  }

  onQuestionTypeChange(type: SurveyQuestionTypeEnum): void {
    const min_characters = this.question_form().controls.min_characters;

    if (type === SurveyQuestionTypeEnum.Text) {
      min_characters.setValue(255);
      min_characters.setValidators(surveyQuestionTextMinCharactersValidators());
    } else {
      min_characters.setValue(null);
      min_characters.clearValidators();
    }

    min_characters.updateValueAndValidity();

    const items = this.question_form().controls.items;

    if (hasEditableResponseOptions(type)) {
      if (items.length === 0) {
        items.push(this.createItemForm(0));
        items.push(this.createItemForm(1));
        this.question_form().markAsDirty();
        this.change_detector.markForCheck();
      }

      return;
    }

    if (items.length > 0) {
      items.clear();
      this.question_form().markAsDirty();
      this.change_detector.markForCheck();
    }
  }

  addItem(): void {
    const items = this.question_form().controls.items;
    items.push(this.createItemForm(items.length));
    this.question_form().markAsDirty();
    this.change_detector.markForCheck();
  }

  removeItem(index: number): void {
    const items = this.question_form().controls.items;

    if (items.length <= 2) {
      return;
    }

    items.removeAt(index);
    this.syncItemOrders();
    this.question_form().markAsDirty();
    this.change_detector.markForCheck();
  }

  dropItem(event: CdkDragDrop<SurveySectionQuestionItemForm[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }

    const items = this.question_form().controls.items;
    const moved_item = items.at(event.previousIndex);
    items.removeAt(event.previousIndex, { emitEvent: false });
    items.insert(event.currentIndex, moved_item, { emitEvent: false });
    this.syncItemOrders();
    this.question_form().markAsDirty();
    this.change_detector.markForCheck();
  }

  private createItemForm(order: number): SurveySectionQuestionItemForm {
    return this.form_builder.group({
      id: this.form_builder.control<number | null>(null),
      statement: this.form_builder.nonNullable.control('', [Validators.required, Validators.maxLength(255)]),
      description: this.form_builder.control<string | null>(null),
      order: this.form_builder.nonNullable.control(order),
    });
  }

  private syncItemOrders(): void {
    this.question_form().controls.items.controls.forEach((item_form, index) => {
      item_form.controls.order.setValue(index, { emitEvent: false });
    });
  }
}

export function surveyQuestionTextMinCharactersValidators(): ValidatorFn[] {
  return [Validators.required, Validators.min(MIN_CHARACTERS_MIN), Validators.max(MIN_CHARACTERS_MAX), Validators.pattern(/^\d+$/)];
}
